import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2, Loader2, Send, ImagePlus, Film, Paperclip, Flag, LinkIcon, X, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AgreementSignModal from "@/components/AgreementSignModal";
import { gateAgreementFromError } from "@/lib/gate";
import { toEmbedUrl } from "@/lib/video";
import { toast } from "sonner";

const Avatar = ({ name, avatar }) =>
  avatar ? <img src={avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
    : <span className="h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] grid place-items-center text-sm font-semibold">{(name || "?").charAt(0).toUpperCase()}</span>;

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const CATEGORIES = [
  { key: "reflection", label: "Reflection" },
  { key: "question", label: "Question" },
  { key: "experience", label: "Lived experience" },
  { key: "photo", label: "Photo" },
  { key: "video", label: "Video" },
];
const CAT_STYLE = {
  reflection: "bg-[#144F75]/10 text-[#144F75]",
  question: "bg-[#D98E73]/15 text-[#b8694f]",
  experience: "bg-[#5C8A72]/15 text-[#5C8A72]",
  photo: "bg-[#8FA89B]/20 text-[#4A5D6B]",
  video: "bg-[#B5794E]/15 text-[#B5794E]",
};
const catLabel = (k) => (CATEGORIES.find((c) => c.key === k) || {}).label || "Reflection";

const isVideoFile = (u) => /\.(mp4|webm|mov|m4v|ogv)$/i.test(u || "");

export default function CommunityTimeline() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [video, setVideo] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [link, setLink] = useState("");
  const [category, setCategory] = useState("reflection");
  const [showLink, setShowLink] = useState(false);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState("");
  const [agreement, setAgreement] = useState(null);
  const [filter, setFilter] = useState("");
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const fileRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["feed", filter],
    queryFn: () => api.get(`/feed${filter ? `?category=${filter}` : ""}`).then((r) => r.data.posts),
    enabled: !!user,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["feed"] });

  const doUpload = async (e, which) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(which);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind === "image") setImage(data.url);
      else if (data.kind === "video") setVideo(data.url);
      else { setFileUrl(data.url); setFileName(data.name); }
      toast.success(`${data.kind === "image" ? "Photo" : data.kind === "video" ? "Video" : "File"} attached`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again.");
    } finally {
      setUploading("");
      if (e.target) e.target.value = "";
    }
  };

  const hasAttachment = image || video || fileUrl;
  const submit = async () => {
    if (!body.trim() && !hasAttachment) return;
    setPosting(true);
    try {
      await api.post("/feed", { body, image_url: image, video_url: video, file_url: fileUrl, file_name: fileName, link_url: link, category });
      setBody(""); setImage(""); setVideo(""); setFileUrl(""); setFileName(""); setLink(""); setShowLink(false); setCategory("reflection"); refresh();
    } catch (e) {
      const ag = await gateAgreementFromError(e);
      if (ag) setAgreement(ag);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPosting(false); }
  };
  const afterSign = async () => { setAgreement(null); await submit(); };

  if (!user) {
    return (
      <div className="eyw-card p-8 text-center" data-testid="timeline-login-prompt">
        <h3 className="font-serif text-2xl text-[#1A242B]">Join the conversation.</h3>
        <p className="text-[#4A5D6B] mt-2 mb-5">The community timeline is for members. Sign in or become a Discovery member to post and connect.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/login" className="eyw-btn">Log in</Link>
          <Link to="/join" className="eyw-btn-ghost">Join Discovery</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="community-timeline">
      {/* Composer */}
      <div className="eyw-card p-5 mb-6">
        <div className="flex gap-3">
          <Avatar name={user.name} avatar={user.avatar} />
          <div className="flex-1">
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="feed-category-picker">
              {CATEGORIES.map((c) => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)} data-testid={`feed-cat-${c.key}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${category === c.key ? "border-[#144F75] " + CAT_STYLE[c.key] : "border-[#E5E0D8] text-[#8FA89B] hover:border-[#144F75]"}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="feed-composer" rows={2}
              placeholder={category === "question" ? "Ask the community a question…" : category === "experience" ? "Share a lived experience…" : "Share a reflection or update…"} className="eyw-input w-full resize-none" />

            {/* Attachment previews */}
            {image && <div className="relative mt-3 inline-block"><img src={image} alt="" className="max-h-56 rounded-xl" /><button type="button" onClick={() => setImage("")} data-testid="feed-remove-image" className="absolute top-2 right-2 bg-black/60 text-white rounded-full h-6 w-6 grid place-items-center"><X size={13} /></button></div>}
            {video && <div className="relative mt-3"><video src={video} controls className="max-h-64 rounded-xl w-full bg-black" data-testid="feed-video-preview" /><button type="button" onClick={() => setVideo("")} data-testid="feed-remove-video" className="absolute top-2 right-2 bg-black/60 text-white rounded-full h-6 w-6 grid place-items-center"><X size={13} /></button></div>}
            {fileUrl && <div className="mt-3 flex items-center gap-2 bg-[#F4F1EB] rounded-xl px-3 py-2 text-sm" data-testid="feed-file-preview"><FileText size={16} className="text-[#144F75]" /> <span className="flex-1 truncate text-[#1A242B]">{fileName}</span><button type="button" onClick={() => { setFileUrl(""); setFileName(""); }} className="text-[#8FA89B] hover:text-red-500"><X size={14} /></button></div>}
            {showLink && <input value={link} onChange={(e) => setLink(e.target.value)} data-testid="feed-link"
              placeholder="Paste a YouTube / Vimeo video or any link" className="eyw-input w-full mt-2 text-sm" />}

            <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => imgRef.current?.click()} disabled={!!uploading} data-testid="feed-upload-photo" className="text-[#144F75] hover:text-[#0d3a57] flex items-center gap-1.5 text-sm disabled:opacity-50">{uploading === "img" ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Photo</button>
                <button type="button" onClick={() => vidRef.current?.click()} disabled={!!uploading} data-testid="feed-upload-video" className="text-[#144F75] hover:text-[#0d3a57] flex items-center gap-1.5 text-sm disabled:opacity-50">{uploading === "vid" ? <Loader2 className="animate-spin" size={16} /> : <Film size={16} />} Video</button>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={!!uploading} data-testid="feed-upload-file" className="text-[#144F75] hover:text-[#0d3a57] flex items-center gap-1.5 text-sm disabled:opacity-50">{uploading === "file" ? <Loader2 className="animate-spin" size={16} /> : <Paperclip size={16} />} File</button>
                <button type="button" onClick={() => setShowLink((v) => !v)} data-testid="feed-toggle-link" className="text-[#144F75] hover:text-[#0d3a57] flex items-center gap-1.5 text-sm"><LinkIcon size={16} /> Link</button>
              </div>
              <button onClick={submit} disabled={posting || !!uploading || (!body.trim() && !hasAttachment)} data-testid="feed-post-btn" className="eyw-btn !py-2 text-sm disabled:opacity-50">
                {posting ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Post
              </button>
            </div>
            <input ref={imgRef} type="file" accept="image/*" onChange={(e) => doUpload(e, "img")} className="hidden" />
            <input ref={vidRef} type="file" accept="video/*" onChange={(e) => doUpload(e, "vid")} className="hidden" />
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.csv,.txt,.ppt,.pptx,.xls,.xlsx" onChange={(e) => doUpload(e, "file")} className="hidden" />
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="feed-filters">
        <button onClick={() => setFilter("")} data-testid="feed-filter-all"
          className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === "" ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`feed-filter-${c.key}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === c.key ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{c.label}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-16 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="space-y-4" data-testid="feed-list">
          {(data || []).map((p) => <FeedPost key={p.id} post={p} onChange={refresh} />)}
          {data?.length === 0 && <p className="text-center text-[#4A5D6B] py-10">No posts yet — be the first to share.</p>}
        </div>
      )}

      {agreement && <AgreementSignModal agreement={agreement} onClose={() => setAgreement(null)} onSigned={afterSign} />}
    </div>
  );
}

function FeedPost({ post, onChange }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.like_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [cbody, setCbody] = useState("");

  const like = async () => {
    try { const { data } = await api.post(`/feed/${post.id}/like`); setLiked(data.liked); setLikes(data.like_count); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadComments = async () => {
    setShowComments((v) => !v);
    if (comments === null) { const { data } = await api.get(`/feed/${post.id}/comments`); setComments(data); }
  };
  const addComment = async () => {
    if (!cbody.trim()) return;
    try { const { data } = await api.post(`/feed/${post.id}/comments`, { body: cbody }); setComments([...(comments || []), data]); setCbody(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/feed/${post.id}`); onChange(); toast.success("Post removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const report = async () => { await api.post(`/feed/${post.id}/report`); toast.success("Reported to moderators. Thank you."); };

  const videoSrc = post.video_url || "";
  const embed = post.link_url ? toEmbedUrl(post.link_url) : "";
  const linkIsVideo = post.link_url && /youtu|vimeo|loom/i.test(post.link_url);

  return (
    <div className="eyw-card p-5" data-testid={`feed-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={post.user_name} avatar={post.user_avatar} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="text-sm flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[#1A242B]">{post.user_name}</span>
              <span className="text-[#8FA89B]">· {timeAgo(post.created_at)}</span>
              {post.category && <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${CAT_STYLE[post.category] || CAT_STYLE.reflection}`} data-testid={`feed-post-cat-${post.id}`}>{catLabel(post.category)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={report} title="Report" className="text-[#B9C4BD] hover:text-[#B5794E]"><Flag size={13} /></button>
              {post.can_delete && <button onClick={del} data-testid={`feed-delete-${post.id}`} className="text-[#B9C4BD] hover:text-red-500"><Trash2 size={14} /></button>}
            </div>
          </div>
          {post.body && <p className="text-[#1A242B] text-sm mt-1 whitespace-pre-wrap">{post.body}</p>}
          {post.image_url && <img src={post.image_url} alt="" className="rounded-xl mt-3 max-h-96 w-full object-cover" />}
          {videoSrc && isVideoFile(videoSrc) && <video src={videoSrc} controls className="rounded-xl mt-3 w-full max-h-96 bg-black" data-testid={`feed-video-${post.id}`} />}
          {post.file_url && <a href={post.file_url} target="_blank" rel="noreferrer" data-testid={`feed-file-${post.id}`} className="mt-3 flex items-center gap-2 bg-[#F4F1EB] rounded-xl px-3 py-2 text-sm hover:bg-[#eee7db] w-fit max-w-full"><FileText size={16} className="text-[#144F75] shrink-0" /> <span className="truncate text-[#144F75]">{post.file_name || "Download file"}</span></a>}
          {embed && linkIsVideo && <div className="aspect-video rounded-xl overflow-hidden mt-3 bg-black"><iframe src={embed} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
          {post.link_url && !linkIsVideo && <a href={post.link_url} target="_blank" rel="noreferrer" className="text-[#144F75] text-sm mt-2 inline-flex items-center gap-1 hover:underline break-all"><LinkIcon size={13} /> {post.link_url}</a>}

          <div className="flex items-center gap-5 mt-3 text-sm">
            <button onClick={like} data-testid={`feed-like-${post.id}`} className={`flex items-center gap-1.5 ${liked ? "text-[#D98E73]" : "text-[#4A5D6B]"} hover:text-[#D98E73]`}>
              <Heart size={16} fill={liked ? "#D98E73" : "none"} /> {likes > 0 && likes}
            </button>
            <button onClick={loadComments} data-testid={`feed-comment-toggle-${post.id}`} className="flex items-center gap-1.5 text-[#4A5D6B] hover:text-[#144F75]">
              <MessageCircle size={16} /> {post.comment_count > 0 && post.comment_count}
            </button>
          </div>

          {showComments && (
            <div className="mt-3 border-t border-[#E5E0D8] pt-3 space-y-3">
              {(comments || []).map((c) => (
                <div key={c.id} className="flex gap-2 text-sm">
                  <Avatar name={c.user_name} avatar={c.user_avatar} />
                  <div><span className="font-semibold text-[#1A242B]">{c.user_name}</span> <span className="text-[#4A5D6B]">{c.body}</span></div>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={cbody} onChange={(e) => setCbody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid={`feed-comment-input-${post.id}`} placeholder="Write a comment…" className="eyw-input flex-1 text-sm" />
                <button onClick={addComment} className="eyw-btn !py-2 !px-3 text-sm"><Send size={14} /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
